/**
 * Exercises as lines — the name, one mark per set, and what came of it. Under a finished session it
 * lists only the ones that deviated, with the rest folded into one line; under an abandoned one it
 * lists all of them, because where the client stopped is the answer.
 *
 * Each set's mark is `model/sessionDetail`'s; the meaning is also in the words at the line's end
 * ("4 × 87,5", "nie zaczęte"), so the marks are decoration for a screen reader.
 */

import { useTranslation } from 'react-i18next';

import srOnly from '~/shared/ui/visuallyHidden.module.css';

import type { ExerciseLine, SetMark } from '../model/sessionDetail';
import { TrophyMark } from './HomeGlyphs';
import styles from './taskBlocks.module.css';

const MARK: Readonly<Record<SetMark, string | undefined>> = {
  done: undefined,
  open: styles.setOpen,
  skipped: styles.setSkipped,
  down: styles.setDown,
  up: styles.setUp,
  record: styles.setRecord,
};

interface ExerciseLinesProps {
  readonly lines: readonly ExerciseLine[];
  /** Exercises left out because they went as planned — one line says how many. */
  readonly unchanged?: number;
  /** At most this many lines; the quick card's height is fixed. */
  readonly limit?: number;
  /**
   * Put the trophy and a spoken „Rekord:" on a record line's figure — the personal-record card,
   * where the record IS the subject and lime alone measures 3.4:1 in Flame, so it is never told by
   * colour only (`docs/design/zadania-progi-2026-10-02/`).
   */
  readonly markRecords?: boolean;
}

export function ExerciseLines({ lines, unchanged = 0, limit, markRecords = false }: ExerciseLinesProps) {
  const { t } = useTranslation();
  const shown = limit === undefined ? lines : lines.slice(0, limit);
  const hidden = lines.length - shown.length;

  return (
    <div className={styles.lines}>
      {shown.map((line) => (
        <div className={styles.line} key={line.key}>
          <span className={styles.lineName}>{line.name}</span>
          <span className={styles.sets} aria-hidden="true">
            {line.marks.map((mark, index) => (
              <i className={`${String(styles.set)} ${MARK[mark] ?? ''}`} key={index} />
            ))}
          </span>
          <span
            className={`${String(styles.lineFigure)} ${
              line.verdict === 'record'
                ? String(styles.lineRecord)
                : line.verdict === 'down' || line.verdict === 'skipped' || line.verdict === 'notStarted'
                  ? String(styles.lineDown)
                  : ''
            }`}
          >
            {markRecords && line.verdict === 'record' ? (
              <>
                <TrophyMark className={styles.lineMark} />
                <span className={srOnly.srOnly}>{t('web:tasks.blocks.record.resultLabel')} </span>
              </>
            ) : null}
            {line.figure ?? t('web:tasks.blocks.session.notStarted')}
          </span>
        </div>
      ))}
      {hidden > 0 || unchanged > 0 ? (
        <p className={styles.lineRest}>
          {[
            hidden > 0 ? t('web:tasks.blocks.session.more', { count: hidden }) : '',
            unchanged > 0 ? t('web:tasks.blocks.session.unchanged', { count: unchanged }) : '',
          ]
            .filter((part) => part !== '')
            .join(' · ')}
        </p>
      ) : null}
    </div>
  );
}
